import { z } from 'zod';
import { inferApiResultSchema } from './infer-api';

export const UserInteractionActionType = {
  IdeaRequireDismiss: 'IDEA_REQUIRE_DISMISS',
  GetStartedClick: 'GET_STARTED_CLICK',
  NewFeedbackSubmissionClick: 'NEW_FEEDBACK_SUBMISSION_CLICK',
  SetupPromptClick: 'SETUP_PROMPT_CLICK',
} as const;

export type UserInteractionActionTypeType =
  (typeof UserInteractionActionType)[keyof typeof UserInteractionActionType];

export const UserInteractionActionTypeSchema = z.nativeEnum(UserInteractionActionType);

/**
 * Stable numeric IDs for each "Getting Started" card.
 * These are stored as `specificRecordId` in the user_interactions table
 * with actionType = GET_STARTED_CLICK.
 */
export const GetStartedStepId = {
  ConnectFeedback: 1,
  ReviewStaleIdeas: 2,
  NotifyStakeholders: 3,
  PrioritizationFramework: 4,
  CustomView: 5,
  PromoteIdeas: 6,
} as const;

export type GetStartedStepIdType = (typeof GetStartedStepId)[keyof typeof GetStartedStepId];

/**
 * Maps the frontend card string ID to its stable numeric step ID.
 */
export const GET_STARTED_CARD_ID_TO_STEP_ID: Record<string, GetStartedStepIdType> = {
  'connect-feedback': GetStartedStepId.ConnectFeedback,
  'review-stale-ideas': GetStartedStepId.ReviewStaleIdeas,
  'notify-stakeholders': GetStartedStepId.NotifyStakeholders,
  'prioritization-framework': GetStartedStepId.PrioritizationFramework,
  'custom-view': GetStartedStepId.CustomView,
  'promote-ideas': GetStartedStepId.PromoteIdeas,
};

/**
 * Stable numeric IDs for each "Setup Prompt" step.
 * These are stored as `specificRecordId` in the user_interactions table
 * with actionType = SETUP_PROMPT_CLICK.
 */
export const SetupPromptStepId = {
  CreateProductArea: 1,
  CreateIntakeForm: 2,
  CreateAnIdea: 3,
  Dismissed: 4,
} as const;

export type SetupPromptStepIdType = (typeof SetupPromptStepId)[keyof typeof SetupPromptStepId];

/**
 * Maps the frontend setup prompt string ID to its stable numeric step ID.
 */
export const SETUP_PROMPT_CARD_ID_TO_STEP_ID: Record<string, SetupPromptStepIdType> = {
  'create-product-area': SetupPromptStepId.CreateProductArea,
  'create-intake-form': SetupPromptStepId.CreateIntakeForm,
  'create-an-idea': SetupPromptStepId.CreateAnIdea,
  'setup-prompt-dismissed': SetupPromptStepId.Dismissed,
};

export const SETUP_PROMPT_TO_GET_STARTED_CARD_ID: Record<string, string> = {
  'create-intake-form': 'connect-feedback',
  'create-an-idea': 'prioritization-framework',
};

export const UserInteractionSchema = z.object({
  id: z.number(),
  accountId: z.string().max(50),
  userId: z.string().max(50),
  actionType: UserInteractionActionTypeSchema,
  specificRecordId: z.number().nullish(),
  archivedAt: z.coerce.date().nullish(),
  deletedAt: z.coerce.date().nullish(),
  updatedAt: z.coerce.date(),
  createdAt: z.coerce.date(),
});

export type UserInteractionDto = z.infer<typeof UserInteractionSchema>;

export const CreateUserInteractionSchema = z.object({
  actionType: UserInteractionActionTypeSchema,
  specificRecordId: z.number().int().optional(),
});

export type CreateUserInteractionDto = z.infer<typeof CreateUserInteractionSchema>;

export const CreateUserInteractionResponseSchema = inferApiResultSchema(UserInteractionSchema);
export type CreateUserInteractionResponseDto = z.infer<typeof CreateUserInteractionResponseSchema>;

export const DEFAULT_USER_INTERACTION_LIMIT = 50;
export const MAX_USER_INTERACTION_LIMIT = 100;

export const UserInteractionFilterSchema = z.object({
  actionType: UserInteractionActionTypeSchema,
  specificRecordId: z.coerce.number().int().optional(),
  limit: z.coerce.number().int().min(1).max(MAX_USER_INTERACTION_LIMIT).optional(),
});

export type UserInteractionFilterDto = z.infer<typeof UserInteractionFilterSchema>;

export const UserInteractionListResponseSchema = inferApiResultSchema(
  z.array(UserInteractionSchema),
);
export type UserInteractionListResponseDto = z.infer<typeof UserInteractionListResponseSchema>;
